import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../Utils/Axios';
import DashboardHeader from '../CompanyComponents/DashboardHeaderRouter';
import PayableKhata from './PayableKhata';
import LoadingScreen from '../../Components/LoadingScreen';
import { useBackspaceNavigation } from '../../hooks/useBackspaceNavigation';

function PayableKhataP2Wrapper() {
  const navigate = useNavigate();

  // Backspace navigation to dashboard
  useBackspaceNavigation();

  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [companyLanguage, setCompanyLanguage] = useState('ur');
  const [bills, setBills] = useState([]);
  const [sales, setSales] = useState([]);
  const [recoveries, setRecoveries] = useState([]);
  const [persons, setPersons] = useState([]);

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const getToken = () => {
    return localStorage.getItem("companyToken");
  };

  const fetchCompanyProfile = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      if (!token) {
        navigate('/company/login');
        return;
      }

      const response = await api.get("/company/profile", {
        headers: { Authorization: `Bearer ${token}` }
      });

      const companyData = response.data.company;
      setCompany(companyData);
      setPersons(companyData.persons || []);
      const lang = companyData.websiteLanguage || companyData.language || 'ur';
      setCompanyLanguage(lang);
      localStorage.setItem('companyLanguage', lang);

      // Use allSettled to ensure all API calls complete even if some fail
      await Promise.allSettled([
        fetchBills(),
        fetchSales(),
        fetchRecoveries()
      ]);
    } catch (error) {
      console.error('Fetch company error:', error);
      toast.error('Failed to load company data');
    } finally {
      setIsLoading(false);
    }
  };

  const getLanguage = () => {
    return companyLanguage || localStorage.getItem('companyLanguage') || 'ur';
  };

  const fetchBills = async () => {
    try {
      const token = getToken();
      const response = await api.get("/bill/company/all", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBills(response.data.bills || []);
    } catch (error) {
      console.error('Fetch bills error:', error);
      const lang = getLanguage();
      const errorMessage = error.response?.status === 500
        ? (lang === 'ur' ? 'بلز لوڈ کرنے میں خرابی' : 'Error loading bills')
        : (lang === 'ur' ? 'بلز لوڈ نہیں ہو سکے' : 'Failed to load bills');
      toast.error(errorMessage, { autoClose: 3000 });
      setBills([]); // Set empty array on error to prevent undefined issues
    }
  };

  const fetchSales = async () => {
    try {
      const token = getToken();
      const response = await api.get("/sale/company/all", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSales(response.data.sales || []);
    } catch (error) {
      console.error('Fetch sales error:', error);
      const lang = getLanguage();
      const errorMessage = error.response?.status === 500
        ? (lang === 'ur' ? 'سیلز لوڈ کرنے میں خرابی' : 'Error loading sales')
        : (lang === 'ur' ? 'سیلز لوڈ نہیں ہو سکے' : 'Failed to load sales');
      toast.error(errorMessage, { autoClose: 3000 });
      setSales([]); // Set empty array on error to prevent undefined issues
    }
  };

  const fetchRecoveries = async () => {
    try {
      const token = getToken();
      const response = await api.get("/recovery/company/all", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecoveries(response.data.recoveries || []);
    } catch (error) {
      console.error('Fetch recoveries error:', error);
      const lang = getLanguage();

      // Check for MongoDB connection errors
      const errorMessage = error.response?.data?.message || error.message || '';
      const isConnectionError = errorMessage.includes('initial connection') ||
        errorMessage.includes('bufferCommands') ||
        errorMessage.includes('MongooseError');

      let errorMsg;
      if (isConnectionError) {
        errorMsg = lang === 'ur'
          ? 'ڈیٹابیس کنکشن کی خرابی۔ براہ کرم تھوڑی دیر بعد دوبارہ کوشش کریں'
          : 'Database connection error. Please try again in a moment';
        toast.error(errorMsg, { autoClose: 5000 });
      } else {
        errorMsg = error.response?.status === 500
          ? (lang === 'ur' ? 'ریکوری لوڈ کرنے میں خرابی' : 'Error loading recoveries')
          : (lang === 'ur' ? 'ریکوری لوڈ نہیں ہو سکے' : 'Failed to load recoveries');
        toast.error(errorMsg, { autoClose: 3000 });
      }

      setRecoveries([]); // Set empty array on error to prevent undefined issues
    }
  };


  const handleLogout = () => {
    localStorage.removeItem("companyToken");
    localStorage.removeItem("companyData");
    navigate("/company/login");
    toast.info(companyLanguage === 'ur' ? "کامیابی سے لاگ آؤٹ ہو گئے" : "Logged out successfully");
  };

  // Get page permissions from company
  const getPagePermission = (pageKey) => {
    if (!company?.pagePermissions) return true; // Default to enabled if no permissions set
    // Convert Map to object if needed, or use directly if already object
    const permissions = company.pagePermissions instanceof Map
      ? Object.fromEntries(company.pagePermissions)
      : company.pagePermissions;
    return permissions[pageKey] !== false; // Default to true if not explicitly false
  };

  // Check permission for payable khata - both keys must be checked
  const hasPermission = getPagePermission('payable-khata-p2') && getPagePermission('payableKhata');

  if (isLoading) {
    return (
      <LoadingScreen
        companyLanguage={companyLanguage}
        message={companyLanguage === "ur" ? "لوڈ ہو رہا ہے..." : "Loading..."}
      />
    );
  }

  // Redirect if no permission
  if (!hasPermission) {
    navigate('/company/dashboard-part2');
    toast.error(companyLanguage === 'ur' ? 'آپ کو اس صفحے تک رسائی نہیں ہے' : 'You do not have access to this page');
    return null;
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
      <DashboardHeader
        company={company}
        companyLanguage={companyLanguage}
        onLogout={handleLogout}
        bills={bills}
        sales={sales}
        recoveries={recoveries}
        persons={persons}
      />
      <div className="relative z-10 max-w-7xl mx-auto px-4 pt-6 pb-8">
        <PayableKhata />
      </div>
    </div>
  );
}

export default PayableKhataP2Wrapper;

